import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import Loader from '../Shared/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faCalendarAlt,
  faArrowLeft,
  faClock,
  faChartPie,
  faCheckCircle,
  faSearch,
  faSyncAlt,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

import { useLanguage } from '../../contexts/LanguageContext';
import { toastHelper } from '../../Helpers/toastHelper';
import WarningAlert from '../Shared/WarningAlert';
const TeacherExamResult = () => {
  const { t } = useLanguage();
  const { examId } = useParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();
  const [deleteSessionLoadingUserId, setDeleteSessionLoadingUserId] = useState(null);
  const [dynamicAlertState, setDynamicAlertState] = useState({
    isOpen: false,
    resolve: null,
    title: "",
    message: "",
    confirmText: "",
    cancelText: "",
    icon: "warning"
  });

  const showDynamicAlert = (options) => {
    return new Promise((resolve) => {
      setDynamicAlertState({
        isOpen: true,
        resolve,
        title: options.title,
        message: options.text,
        confirmText: options.confirmButtonText || t('confirm'),
        cancelText: options.cancelButtonText || t('cancel'),
        icon: options.icon || "warning"
      });
    });
  };

  const handleDynamicAlertConfirm = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: true });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDynamicAlertCancel = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: false });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const fetchResults = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);

    setError(null);
    try {
      const res = await ApiServices.get(`/UserExam/getAllForTeacher/${examId}`);
      if (res.check && Array.isArray(res.data)) {
        setResults(res.data);
      } else {
        setError(t('noResultsForThisExam'));
      }
    } catch (err) {
      setError(t('errorLoadingResults'));
    } finally {
      setLoading(false);
    }
  }, [examId, t]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const filteredResults = useMemo(() => {
    return results.filter(item =>
      item.user_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [results, searchTerm]);

  const stats = useMemo(() => {
    const total = results.length;
    const completed = results.filter(r => r.status === 3).length;
    const notCompleted = total - completed;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, notCompleted, completionRate };
  }, [results]);

  const getAvatarColor = (name) => {
    const colors = ['#6366f1', '#06b6d4', '#8b5cf6', '#ec4899', '#6366f1', '#10b981'];
    const charCode = (name && name.length > 0) ? name.charCodeAt(0) : 0;
    return colors[charCode % colors.length];
  };

  const isUploadedResult = (value) => value === true || value === 'true' || value === 1 || value === '1';

  if (loading) return <Loader text={t('loadingExamResults')} />;

  if (error) return (
    <div className="error-container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="error-card"
      >
        <div className="error-emoji">😕</div>
        <h3>{error}</h3>
        <p>{t('examMightBeNewOrNotSolved')}</p>
        <div className="error-actions">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t('goBack')}
          </button>
          <button className="refresh-btn-secondary" onClick={() => fetchResults()}>
            <FontAwesomeIcon icon={faSyncAlt} />{t('update')}</button>
        </div>
      </motion.div>
      <style jsx>{`
        .error-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }
        .error-card {
          background: white;
          padding: 3rem;
          border-radius: 30px;
          text-align: center;
          box-shadow: 0 20px 50px rgba(0,0,0,0.05);
          max-width: 450px;
          width: 90%;
        }
        .error-emoji { font-size: 4rem; margin-bottom: 1.5rem; }
        h3 { color: #1e293b; margin-bottom: 0.5rem; font-weight: 800; }
        p { color: #64748b; margin-bottom: 2.5rem; }
        .error-actions { display: flex; gap: 1rem; justify-content: center; }
        .back-btn, .refresh-btn-secondary {
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }
        .back-btn { background: #f1f5f9; color: #475569; border: none; }
        .refresh-btn-secondary { background: #3b82f6; color: white; border: none; }
        .back-btn:hover { background: #e2e8f0; }
        .refresh-btn-secondary:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(59, 130, 246, 0.2); }
      `}</style>
    </div>
  );

  return (
    <div className="page-container">
      <div className="content-wrapper">
        <header className="page-header">
          <div className="header-left">
            <button className="back-btn-rect" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{t('addEditMozakaraBack')}</span>
            </button>
            <div className="title-group">
              <h1>{t('studentsResultsBoard')}</h1>
              <p className="subtitle">{t('viewDetailedStudentStats')}</p>
            </div>
          </div>
        </header>

        {/* Stats Section */}
        <div className="stats-section">
          {[
            { label: t('totalStudents'), value: stats.total, icon: faUser, color: 'blue' },
            { label: t('completed'), value: stats.completed, icon: faCheckCircle, color: 'green' },
            { label: t('notCompleted'), value: stats.notCompleted, icon: faClock, color: 'orange' },
            { label: t('completionRate'), value: `${stats.completionRate}%`, icon: faChartPie, color: 'blue-gradient' }
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5, ease: "easeOut" }}
              className={`stat-card-modern ${stat.color}`}
            >
              <div className="icon-wrapper"><FontAwesomeIcon icon={stat.icon} /></div>
              <div className="text-wrapper">
                <h3>{stat.label}</h3>
                <p>{stat.value}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="actions-bar">
          <div className="search-wrap">
            <FontAwesomeIcon icon={faSearch} className="s-icon" />
            <input
              type="text"
              placeholder={t('searchByName')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="results-count">
            {t('found')} <span>{filteredResults.length}</span> {t('result')}
          </div>
        </div>

        {/* Results Grid */}
        <div className="results-grid">
          <AnimatePresence>
            {filteredResults.length > 0 ? (
              filteredResults.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="student-card-modern"
                >
                  <div className="profile-section">
                    <div className="profile-info-left">
                      <div className="avatar" style={{ background: getAvatarColor(item.user_name) }}>
                        {item.user_name ? item.user_name.charAt(0) : '?'}
                      </div>
                      <div className="info">
                        <h3 className="name">{item.user_name}</h3>
                        <span className="exam-tag">{item.subject}</span>
                      </div>
                    </div>
                    <div className={`badge-status ${item.status === 3 ? 'completed' : 'pending'}`}>
                      {item.status === 3
                        ? (isUploadedResult(item.is_uploaded)
                          ? t('gradesUploadedFromFiles')
                          : t('completedFromSystem'))
                        : t('notCompleted')}
                    </div>
                  </div>

                  <div className="details-list">
                    <div className="d-item">
                      <FontAwesomeIcon icon={faClock} />
                      <span>{t('timeLabel')} {item.time_estimation ? `${item.time_estimation} ${t('minute')}` : t('notSpecified')}</span>
                    </div>
                    <div className="d-item">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>{new Date(item.date).toLocaleDateString('ar-EG', { day: 'numeric', month: 'long' })}</span>
                    </div>
                  </div>

                  <div className={`source-pill ${isUploadedResult(item.is_uploaded) ? 'uploaded' : 'system'}`}>
                    <FontAwesomeIcon icon={isUploadedResult(item.is_uploaded) ? faExternalLinkAlt : faCheckCircle} />
                    <span>
                      {t('resultSource')} {isUploadedResult(item.is_uploaded) ? t('uploadedFromFilesByTeacher') : t('fromSystemAfterStudentCompletion')}
                    </span>
                  </div>

                  <div className="score-viz">
                    <div className="score-top">
                      <span className="label">{t('overallPerformance')}</span>
                      <span className="percent">{item.total_questions > 0 ? Math.round((item.score / item.total_questions) * 100) : 0}%</span>
                    </div>
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${item.total_questions > 0 ? (item.score / item.total_questions) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <div className="score-count">
                      <span className="current">{item.score}</span> / {item.total_questions}
                    </div>
                  </div>

                  <div className="card-actions">
                    <button
                      className="action-btn secondary"
                      style={{ width: "100%", color: "#d33", background: "#fee2e2", fontFamily: "Zain, sans-serif", }}
                      disabled={deleteSessionLoadingUserId === item.user_id}
                      onClick={async () => {
                        const result = await showDynamicAlert({
                          title: t('examResetSessionConfirmTitle'),
                          text: t('examResetSessionConfirmText').replace('{name}', item.user_name),
                          icon: 'warning',
                          confirmButtonText: t('examResetSessionConfirmYes'),
                          cancelButtonText: t('cancel'),
                        });
                        if (!result.isConfirmed) return;

                        setDeleteSessionLoadingUserId(item.user_id);
                        try {
                          const res = await ApiServices.delete(`UserExam/Session/Delete/${examId}/${item.user_id}`);
                          if (res.check) {
                            toastHelper.success(t('examResetSessionSuccess').replace('{name}', item.user_name));
                            fetchResults(false);
                          } else {
                            toastHelper.error(t('examResetSessionError'));
                          }
                        } catch (error) {
                          toastHelper.error(error?.msg || t('examResetSessionError'));
                        } finally {
                          setDeleteSessionLoadingUserId(null);
                        }
                      }}
                    >
                      {deleteSessionLoadingUserId === item.user_id ? (
                        <span>{t('loading')}</span>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSyncAlt} />
                          {t('examResetSessionBtn')}
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h3>{t('noResultsFound')}</h3>
                <p>{t('trySearchingWithOtherWords')}</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <WarningAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "warning"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
      />

      <style jsx>{`
        .page-container {
          min-height: 100vh;
          background: #fffefeff;
          padding: 2.5rem 1.5rem;
            font-family: "Zain", sans-serif;
        }
        .content-wrapper { max-width: 1400px; margin: 0 auto; }
        
        /* Header Enhanced */
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 3rem;
        }
        .header-left { display: flex; align-items: center; gap: 2rem; }
        .back-btn-rect {
          background: white;
          border: none;
          padding: 0.8rem 1.2rem;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #475569;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          transition: all 0.2s;
        }
        .back-btn-rect:hover { background: #3b82f6; color: white; transform: translateX(5px); }
        .title-group h1 { font-size: 2.5rem; font-weight: 900; color: #0f172a; margin: 0; letter-spacing: -1px; }
        .subtitle { color: #64748b; margin: 0.2rem 0 0 0; font-size: 1.1rem; }


        /* Stats Section Modern */
        .stats-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 2rem;
          margin-bottom: 3.5rem;
        }
        .stat-card-modern {
          background: white;
          padding: 2rem;
          border-radius: 30px;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          position: relative;
          overflow: hidden;
        }
        .icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        .blue .icon-wrapper { background: #dbeafe; color: #2563eb; }
        .green .icon-wrapper { background: #dcfce7; color: #16a34a; }
        .red .icon-wrapper { background: #fee2e2; color: #dc2626; }
        .orange .icon-wrapper { background: #ffedd5; color: #ea580c; }
        
        .text-wrapper h3 { font-size: 0.95rem; color: #64748b; margin: 0 0 0.25rem 0; font-weight: 600; }
        .text-wrapper p { font-size: 2rem; font-weight: 900; color: #0f172a; margin: 0; }

        /* Actions Bar */
        .actions-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          background: white;
          padding: 0.75rem 1rem;
          border-radius: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.02);
        }
        .search-wrap { position: relative; flex: 1; max-width: 450px; }
        .s-icon { position: absolute; right: 1.25rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .search-wrap input {
          width: 100%;
          padding: 0.8rem 3.5rem 0.8rem 1.5rem;
          border: 1px solid #f1f5f9;
          background: #f8fafc;
          border-radius: 14px;
          font-size: 1rem;
          outline: none;
          transition: all 0.2s;
            font-family: "Zain", sans-serif;

        }
        .search-wrap input:focus { background: white; border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
        .results-count { color: #64748b; font-weight: 600; }
        .results-count span { color: #3b82f6; font-weight: 800; }

        /* Cards Grid Enhanced */
        .results-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }
        .student-card-modern {
          background: white;
          border-radius: 32px;
          padding: 1.75rem;
          position: relative;
          box-shadow: 0 4px 6px rgba(0,0,0,0.02);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          border: 1px solid rgba(255,255,255,1);
          display: flex;
          flex-direction: column;
        }
        .student-card-modern:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 60px rgba(0,0,0,0.08);
          border-color: #e2e8f0;
        }

        .badge-status {
          padding: 0.4rem 1rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 800;
          white-space: nowrap;
        }
        .badge-status.completed { background: #dcfce7; color: #16a34a; }
        .badge-status.pending { background: #fef9c3; color: #a16207; }

        .source-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.7rem 0.9rem;
          margin-bottom: 1rem;
          border-radius: 14px;
          border: 1px solid transparent;
          font-size: 0.85rem;
          font-weight: 700;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
          position: relative;
          overflow: hidden;
        }

        .source-pill :global(svg) {
          color: inherit;
          flex-shrink: 0;
        }

        .source-pill::before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0.16;
          pointer-events: none;
        }

        .source-pill.system {
          background: linear-gradient(90deg, #ecfeff 0%, #f0f9ff 100%);
          border-color: #a5f3fc;
          color: #0f766e;
        }

        .source-pill.system::before {
          background: linear-gradient(90deg, #06b6d4 0%, #0ea5e9 100%);
        }

        .source-pill.uploaded {
          background: linear-gradient(90deg, #fff7ed 0%, #fffbeb 100%);
          border-color: #fed7aa;
          color: #9a3412;
        }

        .source-pill.uploaded::before {
          background: linear-gradient(90deg, #f97316 0%, #6366f1 100%);
        }

        .source-pill span,
        .source-pill :global(svg) {
          position: relative;
          z-index: 1;
        }

        .profile-section { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 2rem; flex-wrap: wrap; }
        .profile-info-left { display: flex; align-items: center; gap: 1.25rem; }
        .avatar {
          width: 64px;
          height: 64px;
          border-radius: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.75rem;
          font-weight: 900;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
        }
        .name { font-size: 1.25rem; font-weight: 800; color: #0f172a; margin: 0; }
        .exam-tag { color: #64748b; font-size: 0.95rem; font-weight: 600; }

        .details-list { display: flex; gap: 1rem; margin-bottom: 2rem; }
        .d-item {
          flex: 1;
          background: #f8fafc;
          padding: 0.6rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.85rem;
          color: #475569;
          font-weight: 700;
        }
        .d-item :global(svg) { color: #94a3b8; }

        .score-viz { margin-bottom: 2rem; }
        .score-top { display: flex; justify-content: space-between; margin-bottom: 0.75rem; }
        .score-top .label { color: #64748b; font-size: 0.9rem; font-weight: 700; }
        .score-top .percent { color: #0f172a; font-weight: 900; font-size: 1.1rem; }
        .progress-bar-bg { background: #f1f5f9; height: 10px; border-radius: 10px; overflow: hidden; margin-bottom: 0.5rem; }
        .progress-bar-fill { height: 100%; background: linear-gradient(90deg, #3b82f6, #6366f1); border-radius: 10px; transition: width 1s ease-out; }
        .score-count { text-align: left; font-size: 0.9rem; color: #94a3b8; font-weight: 700; }
        .score-count .current { color: #0f172a; font-size: 1.1rem; }

        .card-actions { display: flex; width: 100%; gap: 1rem; margin-top: auto; }
        .action-btn {
          padding: 0.8rem;
          border-radius: 14px;
          font-size: 0.9rem;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s;
          border: none;
        }
        .action-btn.secondary { background: #f1f5f9; color: #475569; }
        .action-btn.primary { background: #3b82f6; color: white; }
        .action-btn.secondary:hover { background: #e2e8f0; }
        .action-btn.primary:hover { box-shadow: 0 10px 20px rgba(59, 130, 246, 0.2); transform: scale(1.02); }

        .empty-state { grid-column: 1/-1; text-align: center; padding: 5rem; color: #94a3b8; }
        .empty-icon { font-size: 4rem; margin-bottom: 1rem; }

        @media (max-width: 1100px) {
          .stats-section { grid-template-columns: repeat(2, 1fr); }
          .results-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .page-header { flex-direction: column; align-items: flex-start; gap: 1.5rem; }
          .title-group h1 { font-size: 1.8rem; }
          .stats-section { grid-template-columns: 1fr; }
          .actions-bar { flex-direction: column; gap: 1rem; align-items: flex-start; }
          .results-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default TeacherExamResult;